/**
 * AI 마케팅 인텔리전스 서비스
 *
 * 호출 경로:
 *   Frontend → Java 백엔드 (/api/marketing-ai/*)
 *              → [로컬] FastAPI 직접 포워딩 (demo/*)
 *              → [클라우드] AWS AgentCore Runtime
 *
 * IpScenarioAiService와 동일한 패턴.
 * tenant_schema 는 Java 백엔드 TenantContext에서 자동 주입 — 클라이언트 전달 불필요.
 */

import { restApiIP } from "@plannel/services/utils/rest-api";
import { getAgentLanguage } from "@plannel/utils/i18n";

const BASE = "/api/marketing-ai";

/**
 * 이전 시장 신호 수집 기록 목록 (ai_market_signal)
 * @param {string} statusCd - PENDING | ANALYZED | APPLIED | DISMISSED | null(전체)
 * @param {number} limit    - 최대 반환 건수 (기본 20)
 */
const getSignals = (statusCd = null, limit = 20) =>
  restApiIP.get(`${BASE}/signals`, { params: { ...(statusCd ? { status_cd: statusCd } : {}), limit } });

/**
 * 이전 기록 1건의 전체 상태(event+signals+impact+analysis) 조회 — 재수집 없이 그대로 복원.
 * @param {number} signalId - ai_market_signal.id
 */
const getSignalDetail = (signalId) => restApiIP.get(`${BASE}/signals/${signalId}`);

/**
 * 수집된 시장 신호 단건 삭제 (ai_market_signal + 파생 분석결과)
 * @param {number} signalId - ai_market_signal.id
 */
const deleteSignal = (signalId) => restApiIP.delete(`${BASE}/signals/${signalId}`);

/**
 * 수요 영향 분석 결과 단건 삭제 (ai_demand_analysis_result)
 * @param {number} analysisId - ai_demand_analysis_result.id
 */
const deleteAnalysisResult = (analysisId) => restApiIP.delete(`${BASE}/analysis-results/${analysisId}`);

/**
 * [Live Step 1] 키워드로 시장 신호 수집
 *
 * @param {object}   options        - 수집 옵션
 *   @param {string[]} options.keywords   - 다중 키워드 배열
 *   @param {string}   options.keyword    - 단일 키워드 (keywords 없을 때 fallback)
 *   @param {boolean}  options.useTrends  - Google Trends 포함 여부 (기본 true)
 *   @param {boolean}  options.useNaver   - 네이버 뉴스 포함 여부 (기본 true)
 *   @param {boolean}  options.useGoogle  - Google 뉴스 포함 여부 (기본 true)
 *   @param {string}   options.trendsGeo  - Google Trends 국가 코드 (기본 "KR")
 */
const collectSignals = (options = {}) =>
  restApiIP.post(`${BASE}/collect-signals`, {
    keywords:   options.keywords ?? [],
    keyword:    options.keyword  ?? (options.keywords ?? []).join(" "),
    use_trends:    options.useTrends   ?? true,
    use_naver:     options.useNaver    ?? true,
    use_google:    options.useGoogle   ?? true,
    use_gdelt:     options.useGdelt    ?? true,
    use_scm_press: options.useScmPress ?? true,
    use_newsdata:  options.useNewsdata ?? true,
    // trends_geo 미지정 시 백엔드가 검색어 언어로 국가를 자동 결정 (한글→KR, 영어→US …)
    ...(options.trendsGeo ? { trends_geo: options.trendsGeo } : {}),
    language:   getAgentLanguage(),
  });

/**
 * 멀티 에이전트 수집·검증·분석 — Supervisor 파이프라인 (수집→정제→검증→영향분석→저장).
 * 응답: { signals, item_impacts, demand_impact_pct, signal_id, decisions[], quality{}, summary }
 * @param {object} options
 *   @param {string[]} options.keywords - 사용자가 고른 키워드 목록 (필수)
 *   @param {string} options.channels - 수집 채널 CSV (기본 "naver,google,trends")
 *   @param {string} options.periodStart - 수집 시작일 (YYYY-MM-DD, 양끝 포함)
 *   @param {string} options.periodEnd   - 수집 종료일 (YYYY-MM-DD, 양끝 포함)
 */
const collectAnalyze = (options = {}) =>
  restApiIP.post(`${BASE}/collect-analyze`, {
    // 합치지 않고 그대로 보낸다 — 합친 문자열 하나로 저장되면 복원 때 칩이 1개로 뭉친다
    keywords: options.keywords ?? [],
    // 품목 매칭 전용 검색어(영어) — 수집은 씨드 그대로 돌고 이 값은 품목 검색에만 합류한다
    match_terms: options.matchTerms ?? {},
    channels: options.channels ?? "naver,google,trends,gdelt,scm,newsdata",
    // 산업군 — 조건부 채널(marketingChannels) 판정을 서버가 다시 하기 위해 보낸다
    ...(options.industry ? { industry: options.industry } : {}),
    // 구간만 보낸다 — 프리셋 칩도 today 기준 구간으로 바뀌므로 일수는 서버가 구간에서 구한다
    ...(options.periodStart && options.periodEnd
      ? { period_start: options.periodStart, period_end: options.periodEnd } : {}),
    language: getAgentLanguage(),
  });

/** 품목 매핑 데이터 통합 구축 — 키워드 분류 → 임베딩 인덱스 (증분, 원버튼) — 관리용 */
const mappingDataBuild = (options = {}) =>
  restApiIP.post(`${BASE}/mapping-data/build`, { rebuild: options.rebuild ?? false });

/** 품목 키워드 분류 현황 — { classified_count, unclassified_count, industry_count } */
const classifyStatus = () => restApiIP.get(`${BASE}/classify-status`);

/** 품목 키워드 LLM 분류 실행 (증분. force=true 면 전체 재분류) — 관리용 */
const classifyItems = (options = {}) =>
  restApiIP.post(`${BASE}/classify-items`, {
    batch_size: options.batchSize ?? 20,
    force:      options.force ?? false,
  });

/** 품목 임베딩 인덱스 상태 — { embedded, total_items, last_updated } */
const embeddingIndexStatus = () => restApiIP.get(`${BASE}/embedding-index/status`);

/** 품목 임베딩 인덱스 구축 (증분. rebuild=true 면 전체 재임베딩) — 관리용 */
const embeddingIndexBuild = (options = {}) =>
  restApiIP.post(`${BASE}/embedding-index/build`, {
    rebuild: options.rebuild ?? false,
    limit:   options.limit ?? 5000,
  });

/**
 * 멀티 에이전트 수집·검증·분석 — 진행 상황 실시간 SSE.
 * Supervisor 도구 진행이 이벤트로 흘러온다:
 *   {type:"start"} → {type:"tool_start", tool, label} / {type:"tool_done", tool, status}
 *   → {type:"result", data:{...}} → {type:"done"}   (오류: {type:"error", content})
 * @param {object}   options          - { keywords, channels, periodStart, periodEnd }
 * @param {object}   handlers         - { onEvent(evt), signal(AbortSignal) }
 */
const collectAnalyzeStream = async (options = {}, { onEvent, signal } = {}) => {
  const authService = (await import("@plannel/services/system/auth-service")).default;
  const user = authService.getCurrentUser();
  const headers = { "Content-type": "application/json", Accept: "text/event-stream" };
  if (user && user.accessToken) {
    headers["x-tenant-id"] = user.tenantId;
    headers.Authorization = `${user.type} ${user.accessToken}`;
  }
  const res = await fetch(`${BASE}/collect-analyze/stream`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      // 합치지 않고 그대로 보낸다 — 합친 문자열 하나로 저장되면 복원 때 칩이 1개로 뭉친다
      keywords: options.keywords ?? [],
      match_terms: options.matchTerms ?? {},   // 품목 매칭 전용 검색어 — 수집은 씨드 그대로
      channels: options.channels ?? "naver,google,trends,gdelt,scm,newsdata",
      ...(options.industry ? { industry: options.industry } : {}),   // 조건부 채널 판정용
      // 구간만 보낸다 — 프리셋 칩도 today 기준 구간으로 바뀌므로 일수는 서버가 구간에서 구한다
      ...(options.periodStart && options.periodEnd
        ? { period_start: options.periodStart, period_end: options.periodEnd } : {}),
      language: getAgentLanguage(),
    }),
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`SSE 연결 실패: HTTP ${res.status}`);

  const reader  = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  // SSE 이벤트는 빈 줄("\n\n") 구분, 각 줄 "data:" 접두 후 JSON
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buf.indexOf("\n\n")) >= 0) {
      const rawEvent = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataStr = rawEvent
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trim())
        .join("");
      if (!dataStr) continue;
      try {
        onEvent?.(JSON.parse(dataStr));
      } catch {
        /* 파싱 불가 조각은 무시 */
      }
    }
  }
};

/**
 * [Live Step 2] 수집된 event + 사용자가 선택한 신호 인덱스로 수요 분석
 * @param {object}      eventData           - collect-signals 응답의 event 객체
 * @param {number|null} selectedSignalIndex - 선택한 신호 인덱스 (0-based)
 * @param {string}      userComment         - 사용자 추가 코멘트
 * @param {Array<{name: string, content_b64: string}>} contextFiles
 *        - 첨부 문서 목록 (pdf·docx·xlsx·pptx). 비어 있으면 body 에서 생략한다.
 */
const runDemoWithEvent = (eventData, selectedSignalIndex, userComment = "", contextFiles = []) =>
  restApiIP.post(`${BASE}/run`, {
    event_data:            eventData,
    selected_signal_index: selectedSignalIndex,
    language:              getAgentLanguage(),
    user_comment:          userComment || "",
    ...(contextFiles.length ? { context_files: contextFiles } : {}),
    use_real_mcp:          false,
  });

/**
 * 직접 입력 키워드에 대한 파생 검색어 추천 (LLM)
 * @param {string} keyword - 씨드 키워드
 * @param {number} limit   - 추천 개수 (기본 5)
 */
const suggestKeywords = (keyword, limit = 5) =>
  restApiIP.post(`${BASE}/suggest-keywords`, { keyword, limit, language: getAgentLanguage() });

/**
 * AI 추천 — ai_demand_analysis_result 목록 조회
 * @param {string} reviewStatus - "PENDING_REVIEW" | "APPROVED" | "REJECTED" | null(전체)
 */
const getAnalysisResults = (reviewStatus = "PENDING_REVIEW") =>
  restApiIP.get(`${BASE}/analysis-results`, { params: { review_status: reviewStatus } });

/**
 * AI 추천 승인/거절 상태 업데이트
 * @param {number} analysisId  - ai_demand_analysis_result.id
 * @param {string} status      - "APPROVED" | "REJECTED"
 */
const updateAnalysisReviewStatus = (analysisId, status) =>
  restApiIP.post(`${BASE}/analysis-results/${analysisId}/review`, { review_status: status });

const aiScenarioDemoService = {
  getSignals, getSignalDetail, deleteSignal, collectSignals, collectAnalyze, collectAnalyzeStream,
  mappingDataBuild, classifyStatus, classifyItems, embeddingIndexStatus, embeddingIndexBuild, suggestKeywords, runDemoWithEvent,
  getAnalysisResults, updateAnalysisReviewStatus, deleteAnalysisResult,
};
export default aiScenarioDemoService;
